import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, RefreshCw, CheckCircle, Clock, AlertTriangle,
  ChevronRight, ArrowUpDown, Search, TrendingUp, Shield,
} from 'lucide-react';
import { companiesApi, CompanyFramework } from '../../api';
import PageInfoButton from '@/components/PageInfoButton';

type SortKey = 'name' | 'progress' | 'assigned_at' | 'pending';

function approvalPct(f: CompanyFramework) {
  return f.total_pages > 0 ? Math.round((f.approved_count / f.total_pages) * 100) : 0;
}

function overallStatus(f: CompanyFramework): { label: string; style: string } {
  const pct = approvalPct(f);
  if (pct === 100) return { label: 'Certified', style: 'bg-emerald-100 text-emerald-700' };
  if (f.submitted_count > 0) return { label: 'Under Review', style: 'bg-amber-100 text-amber-700' };
  if (f.in_progress_count > 0 || f.approved_count > 0) return { label: 'In Progress', style: 'bg-blue-100 text-blue-700' };
  return { label: 'Not Started', style: 'bg-slate-100 text-slate-600' };
}

const CompanyFrameworkList: React.FC = () => {
  const [frameworks, setFrameworks] = useState<CompanyFramework[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('assigned_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      setFrameworks(await companiesApi.getCompanyFrameworks());
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const cycleSort = (key: SortKey) => {
    if (sort === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSort(key); setSortDir('desc'); }
  };

  const sorted = useMemo(() => {
    let list = frameworks.filter(f =>
      !search || f.name.toLowerCase().includes(search.toLowerCase()) ||
      (f.description || '').toLowerCase().includes(search.toLowerCase())
    );
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sort === 'name') cmp = a.name.localeCompare(b.name);
      else if (sort === 'progress') cmp = approvalPct(a) - approvalPct(b);
      else if (sort === 'assigned_at') cmp = new Date(a.assigned_at).getTime() - new Date(b.assigned_at).getTime();
      else if (sort === 'pending') cmp = a.submitted_count - b.submitted_count;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [frameworks, search, sort, sortDir]);

  // Totals
  const totalPages = frameworks.reduce((s, f) => s + f.total_pages, 0);
  const totalApproved = frameworks.reduce((s, f) => s + f.approved_count, 0);
  const totalSubmitted = frameworks.reduce((s, f) => s + f.submitted_count, 0);
  const totalInProgress = frameworks.reduce((s, f) => s + f.in_progress_count, 0);
  const overallApprovalPct = totalPages > 0 ? Math.round((totalApproved / totalPages) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Compliance Frameworks</h1><PageInfoButton /></div>
          <p className="text-slate-500 text-sm mt-0.5">Track progress across all assigned frameworks</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* ── Portfolio summary ────────────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-5">
        <div className="flex flex-wrap items-center gap-6">
          {/* Big approval score */}
          <div className="flex items-center gap-4 pr-6 border-r border-slate-100">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-xl
              ${overallApprovalPct >= 80 ? 'bg-emerald-100 text-emerald-700' :
                overallApprovalPct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
              {overallApprovalPct}%
            </div>
            <div>
              <div className="font-semibold text-slate-800">Overall Approval</div>
              <div className="text-xs text-slate-500">{totalApproved} of {totalPages} pages approved</div>
            </div>
          </div>

          {/* Aggregated stats */}
          <div className="flex flex-wrap gap-6 flex-1">
            {[
              { label: 'Frameworks', value: frameworks.length, icon: <Shield className="w-4 h-4 text-indigo-500" /> },
              { label: 'Total Pages', value: totalPages, icon: <FileText className="w-4 h-4 text-slate-400" /> },
              { label: 'Approved', value: totalApproved, icon: <CheckCircle className="w-4 h-4 text-emerald-500" /> },
              { label: 'Submitted', value: totalSubmitted, icon: <TrendingUp className="w-4 h-4 text-amber-500" /> },
              { label: 'In Progress', value: totalInProgress, icon: <RefreshCw className="w-4 h-4 text-blue-500" /> },
              { label: 'Not Started', value: frameworks.reduce((s, f) => s + f.not_started_count, 0), icon: <Clock className="w-4 h-4 text-slate-400" /> },
            ].map(s => (
              <div key={s.label} className="text-center">
                <div className="flex items-center gap-1 justify-center mb-0.5">{s.icon}</div>
                <div className="text-xl font-bold text-slate-900">{s.value}</div>
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Portfolio progress bar */}
          <div className="w-full mt-3">
            <div className="flex gap-0.5 h-2.5 rounded-full overflow-hidden bg-slate-100">
              {totalPages > 0 && <>
                <div className="bg-emerald-500" style={{ width: `${(totalApproved / totalPages) * 100}%` }} title={`Approved: ${totalApproved}`} />
                <div className="bg-amber-400" style={{ width: `${(totalSubmitted / totalPages) * 100}%` }} title={`Submitted: ${totalSubmitted}`} />
                <div className="bg-blue-400" style={{ width: `${(totalInProgress / totalPages) * 100}%` }} title={`In Progress: ${totalInProgress}`} />
              </>}
            </div>
            <div className="flex gap-4 mt-2 text-xs text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500 inline-block" />Approved</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-400 inline-block" />Submitted</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-400 inline-block" />In Progress</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-200 inline-block" />Not Started</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Controls ────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search frameworks…"
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <ArrowUpDown className="w-3.5 h-3.5" /> Sort:
        </div>
        {([
          { key: 'assigned_at', label: 'Date Assigned' },
          { key: 'progress', label: 'Progress' },
          { key: 'pending', label: 'Pending Reviews' },
          { key: 'name', label: 'Name' },
        ] as { key: SortKey; label: string }[]).map(opt => (
          <button
            key={opt.key}
            onClick={() => cycleSort(opt.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
              sort === opt.key
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            {opt.label} {sort === opt.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
          </button>
        ))}
        <span className="text-xs text-slate-400 ml-auto">{sorted.length} framework{sorted.length !== 1 ? 's' : ''}</span>
      </div>

      {/* ── Framework cards ─────────────────────────────────────────────────── */}
      {sorted.length === 0 ? (
        <div className="bg-white border rounded-xl py-20 text-center">
          <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <h3 className="font-semibold text-slate-700">
            {search ? 'No frameworks match your search' : 'No frameworks assigned'}
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            {search ? 'Try a different search term' : 'Contact your platform administrator to assign frameworks.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map(f => {
            const pct = approvalPct(f);
            const status = overallStatus(f);
            const hasReviews = f.submitted_count > 0;
            const progressPct = f.total_pages > 0 ? {
              approved: (f.approved_count / f.total_pages) * 100,
              submitted: (f.submitted_count / f.total_pages) * 100,
              in_progress: (f.in_progress_count / f.total_pages) * 100,
              not_started: (f.not_started_count / f.total_pages) * 100,
            } : { approved: 0, submitted: 0, in_progress: 0, not_started: 100 };

            return (
              <div key={f.id} className="bg-white border rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">

                  {/* Approval score circle */}
                  <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center font-bold text-lg leading-none
                    ${pct >= 80 ? 'bg-emerald-100 text-emerald-700' :
                      pct >= 50 ? 'bg-blue-100 text-blue-700' :
                      pct > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                    {pct}%
                    <span className="text-[9px] font-medium opacity-70 mt-0.5">approved</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Title row */}
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div>
                        <h3 className="font-semibold text-slate-900 text-base">{f.name}</h3>
                        {f.description && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{f.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {hasReviews && (
                          <span className="flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
                            <AlertTriangle className="w-3 h-3" />
                            {f.submitted_count} pending review{f.submitted_count !== 1 ? 's' : ''}
                          </span>
                        )}
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${status.style}`}>
                          {status.label}
                        </span>
                      </div>
                    </div>

                    {/* 4-segment progress bar */}
                    <div className="flex gap-0.5 h-2 rounded-full overflow-hidden bg-slate-100 my-2.5">
                      <div className="bg-emerald-500 transition-all" style={{ width: `${progressPct.approved}%` }} />
                      <div className="bg-amber-400 transition-all" style={{ width: `${progressPct.submitted}%` }} />
                      <div className="bg-blue-400 transition-all" style={{ width: `${progressPct.in_progress}%` }} />
                    </div>

                    {/* Stats row */}
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex gap-3 text-xs">
                        <span className="flex items-center gap-1 text-emerald-700 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                          {f.approved_count} approved
                        </span>
                        <span className="flex items-center gap-1 text-amber-600 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                          {f.submitted_count} submitted
                        </span>
                        <span className="flex items-center gap-1 text-blue-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
                          {f.in_progress_count} in progress
                        </span>
                        <span className="flex items-center gap-1 text-slate-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 inline-block" />
                          {f.not_started_count} not started
                        </span>
                        <span className="text-slate-300">·</span>
                        <span className="text-slate-400">{f.total_pages} total</span>
                      </div>

                      <div className="ml-auto flex items-center gap-2">
                        <span className="text-xs text-slate-400">
                          Assigned {new Date(f.assigned_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        {hasReviews && (
                          <Link
                            to={`/company/frameworks/${f.id}/review`}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                            Review {f.submitted_count}
                          </Link>
                        )}
                        <Link
                          to={`/company/frameworks/${f.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          View Details <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CompanyFrameworkList;
